import {defaultDescription, defaultTitle, HomeyRoute} from "../../app.routes";
import {redirectOnUserState} from "../../../router.helper";
import {LandingPageComponent} from "./landing-page.component";
import {AllApartmentsPageComponent} from "../extra-pages/all-apartments-page/all-apartments-page.component";
import {inject} from "@angular/core";
import {ApartmentService} from "../../services/apartment.service";
import {map, of, switchMap} from "rxjs";
import {
  PublicApartmentDetailPageComponent
} from "./public-apartment-detail-page/public-apartment-detail-page.component";
import {
  ApartmentSearchDetailPageComponent
} from "./apartment-search-detail-page/apartment-search-detail-page.component";
import {initialSignupRoute, initialSignupRouteIncludingInfo} from "../signup/specific-signup.routes";
import {formatDate} from "@angular/common";
import {UnitAdvertisementDto} from "homey-housing-lottery-app-dto";
import {getAmenityLabel} from "../../pipes/amenity-label.pipe";
import {getMinRent} from "../../pipes/min-rent.pipe";
import {getMaxRent} from "../../pipes/max-rent.pipe";
import {environment} from "../../../../environments/environment";
import {boroughToText} from "../../pipes/borough-to-text.pipe";
import {unitTypeToText} from "lottery-shared";
import {ActivatedRouteSnapshot, CanMatchFn, ParamMap, Route, UrlSegment} from "@angular/router";
import {Borough, boroughs} from "../../model/borough";
import {isNative, retryImport} from "../../utils";
import {MobileCommonQuestionsComponent} from "./mobile-common-questions/mobile-common-questions.component";
import {getApartmentCanonical} from "../../router-utils";
import {isUserInStates} from "../../../router.helper";

function titleCase(str: string) {
  const splitStr = str.toLowerCase().split(' ');
  for (let i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  return splitStr.join(' ');
}

const isValidBorough: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
) => {
  const boroughsString: readonly string[] = boroughs;
  return boroughsString.includes(segments[segments.length - 1].path);
};

const publicApartmentRoutes: HomeyRoute = {
  path: 'apartments',
  data: {
    headerStyle: 'landing-sub-page',
  },
  children: [
    {
      path: '',
      pathMatch: 'full',
      // i18n title
      title: () => 'Affordable Housing Lottery Apartments - Homey NYC',
      component: AllApartmentsPageComponent,
      data: {
        index: true,
        // i18n meta description
        metaDescription: () => 'All NYC Affordable Housing Apartment Lotteries on NYC Housing Connect — Check HPD/HDC eligibility and apply before they close.',
        structuredData: () => {
          return of({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": environment.baseUrl
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Apartments",
                    "item": `${environment.baseUrl}/apartments`
                  }
                ]
              }]
          })
        }
      }
    },
    {
      path: 'boroughs/:borough',
      // i18n title with param
      title: (route: ActivatedRouteSnapshot) => {
        const borough = boroughToText(route.paramMap.get('borough') as Borough);
        return `Affordable Housing Lottery Apartments in ${borough} - Homey NYC`;
      },
      component: AllApartmentsPageComponent,
      data: {
        index: true,
        // i18n meta description with param
        metaDescription: (route: ActivatedRouteSnapshot) => {
          const borough = boroughToText(route.paramMap.get('borough') as Borough);
          return `All ${borough} Affordable Housing Apartment Lotteries on NYC Housing Connect — Check HPD/HDC eligibility and apply before they close.`;
        },
      },
      canMatch: [isValidBorough]
    },
    {
      path: ':lotteryId',
      component: PublicApartmentDetailPageComponent,
      // i18n dynamic title (keeps your original logic)
      title: (route: ActivatedRouteSnapshot) => {
        const apartmentService = inject(ApartmentService);
        const lotteryId = route.paramMap.get('lotteryId');

        if (!lotteryId) {
          return of(defaultTitle);
        }

        const lottery$ = apartmentService.getApartment(lotteryId);
        return lottery$.pipe(
          map(lottery => {
            if (!(lottery.name.match(/^\d/))) {
              const address = (lottery.addressInfo.address.toLowerCase().split(',').pop() ?? '')
                .replace('avenue', 'ave')
                .replace('street', 'st')
                .replace('boulevard', 'blvd')
                .replace('place', 'pl')
                .replace('court', 'ct')
                .replace('road', 'rd')
                .replace('way', 'wy')
                .replace('parkway', 'pkwy')
                .replace('drive', 'dr');

              return `Apply to the ${titleCase(address)}, ${lottery.name} Housing Lottery`;
            } else {
              return `Apply to the ${lottery.name} Affordable Housing Lottery`;
            }
          })
        );
      },
      data: {
        index: true,
        signupToApplyRoute: initialSignupRouteIncludingInfo,
        signupToApplyText: 'Check eligibility',
        showPromotionHeader: true,
        canonicalUrl: getApartmentCanonical,
        signupToApplyRouteParams: (data: ParamMap) => {
          const id = data.get('lotteryId');

          if (id) {
            return inject(ApartmentService).getApartment(id).pipe(
              map(lottery => {
                return {lotteryName: lottery.name}
              }))
          }
          return of({});
        },
        structuredData: (data: ParamMap) => {
        const id = data.get('lotteryId');

          if (id) {
            return inject(ApartmentService).getApartment(id).pipe(
              map(lottery => {
                return {
                  "@context": "https://schema.org",
                  "@graph": [
                    {
                      "@type": "ApartmentComplex",
                      "name": `${lottery.name} Affordable Housing Lottery`,
                      "url": typeof window !== 'undefined' ? window.location.href : environment.baseUrl,
                      "image": lottery.mainPhoto.baseImage,
                      "address": {
                        "@type": "PostalAddress",
                        "streetAddress": lottery.addressInfo.address,
                        "addressLocality": "New York",
                        "addressRegion": "NY",
                        "postalCode": lottery.addressInfo.zip,
                        "addressCountry": "US"
                      },
                      "amenityFeature": [
                        lottery.amenities
                          .map(getAmenityLabel)
                          .filter((label): label is string => label !== undefined)
                          .map((label) => ({
                            "@type": "LocationFeatureSpecification",
                            "name": label,
                            "value": true,
                          }))
                      ],
                      "offers": {
                        "@type": "Offer",
                        "availabilityEnds": formatDate(lottery.endDate, 'yyyy-MM-dd', 'en-US'),
                        "priceCurrency": "USD"
                      }
                    },
                    {
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                        {
                          "@type": "ListItem",
                          "position": 1,
                          "name": "Home",
                          "item": environment.baseUrl
                        },
                        {
                          "@type": "ListItem",
                          "position": 2,
                          "name": "Apartments",
                          "item": `${environment.baseUrl}/apartments`
                        },
                        {
                          "@type": "ListItem",
                          "position": 3,
                          "name": `${lottery.name} Affordable Housing Lottery`,
                          "item": `${environment.baseUrl}/apartments/${lottery.slug ?? lottery.id}`
                        }
                      ]
                    }],
                }
              }))
          }
          return undefined;
        },
        metaDescription: (data: ActivatedRouteSnapshot) => {
          const id = data.paramMap.get('lotteryId');

          if (id) {
            return inject(ApartmentService).getApartment(id).pipe(
              switchMap(lottery => {
                const deadline = formatDate(lottery.endDate, 'MMMM d', 'en-US');
                let rentClause = '';

                if (lottery.type === 'senior') {
                  rentClause = '30% of gross income.';
                } else {
                  const sorted = [...lottery.unitTypes].sort(
                    (a, b) => a.monthlyRent - b.monthlyRent,
                  );

                  const uniqueByType = new Map<string, UnitAdvertisementDto>();
                  for (const ad of sorted) {
                    if (!uniqueByType.has(ad.unitType)) {
                      uniqueByType.set(ad.unitType, ad);
                    }
                  }

                  const uniqueAds = Array.from(uniqueByType.values());

                  const first = uniqueAds[0];
                  const second = uniqueAds[1];

                  if (first) {
                    rentClause = `${unitTypeToText(first.unitType)}`;
                    if(first.type !== 'senior') {
                      rentClause += ` from $${first.monthlyRent}`;
                    }
                    if (second) {
                      rentClause += `, ${unitTypeToText(second.unitType)}`;
                      if(second.type !== 'senior') {
                        rentClause += ` from $${second.monthlyRent}`;
                      }
                    }
                    rentClause += '.';
                  }
                }

                const boroughString = boroughToText(lottery.borough as Borough);
                const address = lottery.addressInfo.address;
                const neighborhood = lottery.neighborhood;

                return of(`Apply by ${deadline} for ${lottery.name}'s new affordable apartments in ${address}, ${neighborhood}, ${boroughString}, NY. ${rentClause} Check your eligibility instantly.`);
              }),
            )
          }

          return of(defaultDescription);
        },
      }
    },
  ],
};
export const landingPageRoute: HomeyRoute = {
  path: '',
  runGuardsAndResolvers: 'always',
  children: [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () => import('../signup/intro/native-landing-page/native-landing-page.component').then(m => m.NativeLandingPageComponent),
      canMatch: [() => isNative, isUserInStates(['unauthorized', 'signup-flow'])],
      data: {
        backgroundType: 'blue-gradient',
        headerStyle: 'hidden',
      }
    },
    {
      path: '',
      data: {
        headerStyle: 'landing',
        index: true,
        showPromotionHeader: true,
      },
      pathMatch: 'full',
      component: LandingPageComponent,
      runGuardsAndResolvers: 'always',
      canActivate: [
        redirectOnUserState((router, user) => {
          switch (user.status) {
            case "unauthorized":
            case "signup-flow":
              return true;
            case "active":
              return router.createUrlTree(['applications']);
            case "mandatory-flow":
              return router.createUrlTree(['profile']);
          }
        })
      ]
    },
    {
      path: 'mobile-faq',
      data: {
        headerStyle: 'landing-sub-page',
      },
      component: MobileCommonQuestionsComponent
    },
    {
      path: 'apartments-search',
      children: [
        {
          path: ':lotteryId',
          component: ApartmentSearchDetailPageComponent,
          data: {
            signupToApplyRoute: initialSignupRoute,
            index: true,
            canonicalUrl: getApartmentCanonical,
            showCarouselEligibilityCta: true,
          }
        },
      ]
    },
    publicApartmentRoutes,
    {
      path: 'signup',
      title: () => 'Sign Up for Homey NYC - Homey Housing Lottery Made Easy',
      loadChildren: retryImport(() => import('../signup-section/signup.routes').then(m => m.signupRoutes)),
      data: {
        headerStyle: 'sub-page-with-logo',
        metaDescription: () => 'Join Homey today to effortlessly apply to NYC affordable housing lotteries. Automate applications, eliminate paperwork, and maximize your winning chances for low income housing via Housing Connect.'
      }
    }
  ]
};
